import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DetectMethodChanges} from '../utils';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {TradingInstrumentKind} from '../trading-instruments/trading-instrument-kind.enum';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {MessageBusService} from '../message-bus.service';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {hedgeProductRow, PHA_AVAILABLE_HEDGE_PRODUCT_COLUMNS, PHA_AVAILABLE_HEDGE_PRODUCT_TICKERS, PhaHedgeProductRow} from './pha-hedge-products.model';

@Component({
    selector: 'ets-pha-hedge-products-dialog',
    templateUrl: './pha-hedge-products-dialog.component.html',
    styleUrls: ['./pha-hedge-products-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaHedgeProductsDialogComponent implements OnInit, OnDestroy {
    constructor(private readonly _changeDetector: ChangeDetectorRef,
                private readonly _instruments: TradingInstrumentsService,
                private readonly _quotes: LastQuoteCacheService,
                private readonly _bus: MessageBusService) {}

    visible = false;
    readonly columns = PHA_AVAILABLE_HEDGE_PRODUCT_COLUMNS;
    rows: {[ticker: string]: PhaHedgeProductRow} = {};
    private readonly _unsub = new Subject<void>();
    private _subscribed: string[] = [];
    private _destroyed = false;

    ngOnInit(): void {
        this._bus.of<QuoteDto[]>('QuoteDto').pipe(takeUntil(this._unsub)).subscribe(message => {
            const quotes = message.payload || [];
            if (this.visible && quotes.some(quote => !!this.rows[quote.ticker])) { this.applyQuotes(quotes); }
        });
    }

    @DetectMethodChanges()
    open(): void {
        if (this._destroyed || this.visible) { return; }
        this.rows = {};
        for (const ticker of PHA_AVAILABLE_HEDGE_PRODUCT_TICKERS) {
            const instrument = this._instruments.getInstrumentByTicker(ticker);
            this.rows[ticker] = hedgeProductRow(ticker, instrument, this._quotes.getLastQuote(ticker));
        }
        this._subscribed = PHA_AVAILABLE_HEDGE_PRODUCT_TICKERS.filter(ticker => {
            const instrument = this._instruments.getInstrumentByTicker(ticker);
            return instrument && (instrument.kind === TradingInstrumentKind.Stock || instrument.kind === TradingInstrumentKind.Index);
        }).sort((left, right) => {
            // Proxy shells seed streams sequentially. Load ETFs before index lookups that may time out.
            const priority = (ticker: string) => this._instruments.getInstrumentByTicker(ticker).kind === TradingInstrumentKind.Stock ? 0 : 1;
            return priority(left) - priority(right)
                || PHA_AVAILABLE_HEDGE_PRODUCT_TICKERS.indexOf(left) - PHA_AVAILABLE_HEDGE_PRODUCT_TICKERS.indexOf(right);
        });
        this.visible = true;
        this._quotes.subscribeTickers(this._subscribed);
    }

    @DetectMethodChanges()
    close(): void {
        this.visible = false;
        this.releaseQuotes();
        this.rows = {};
    }

    @DetectMethodChanges()
    private applyQuotes(quotes: QuoteDto[]): void {
        let next = this.rows;
        for (const quote of quotes) {
            if (!this.rows[quote.ticker]) { continue; }
            if (next === this.rows) { next = {...this.rows}; }
            next[quote.ticker] = hedgeProductRow(quote.ticker, this._instruments.getInstrumentByTicker(quote.ticker), quote);
        }
        this.rows = next;
    }

    private releaseQuotes(): void {
        if (this._subscribed.length) { this._quotes.unsubscribeTickers(this._subscribed); }
        this._subscribed = [];
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this.visible = false;
        this.releaseQuotes();
        this._unsub.next();
        this._unsub.complete();
    }
}
